import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  styled,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ArchitectureOutlinedIcon from "@mui/icons-material/ArchitectureOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import LetsTalk from "../../components/letsTalk";
import PageTitle from "../../components/PageTitle";

const brandedPlaceholder = (label: string): string => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 400'><defs><linearGradient id='g' x1='0' x2='1' y1='0' y2='1'><stop offset='0' stop-color='#2f81ad'/><stop offset='1' stop-color='#1d648c'/></linearGradient></defs><rect width='600' height='400' fill='url(%23g)'/><text x='50%' y='50%' fill='white' font-size='26' font-family='-apple-system, system-ui, sans-serif' text-anchor='middle' dominant-baseline='middle' font-weight='700'>${label}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const HeroBg = styled(Box)(({ theme }) => ({
  backgroundColor: "#0d1620",
  color: "#fff",
  paddingTop: theme.spacing(14),
  paddingBottom: theme.spacing(11),
  [theme.breakpoints.down("md")]: {
    paddingTop: theme.spacing(11),
    paddingBottom: theme.spacing(8),
  },
}));

const HeroLabel = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#7ec1e2",
  marginBottom: 22,
});

const HeroTitle = styled(Typography)(({ theme }) => ({
  fontSize: "3.2rem",
  fontWeight: 700,
  lineHeight: 1.12,
  letterSpacing: "-0.02em",
  color: "#fff",
  marginBottom: theme.spacing(3),
  maxWidth: 800,
  [theme.breakpoints.down("md")]: { fontSize: "2.4rem" },
  [theme.breakpoints.down("sm")]: { fontSize: "1.9rem" },
}));

const HeroLead = styled(Typography)(({ theme }) => ({
  fontSize: "1.08rem",
  lineHeight: 1.65,
  color: "rgba(255, 255, 255, 0.72)",
  marginBottom: theme.spacing(4),
  maxWidth: 640,
}));

const PrimaryButton = styled(Button)({
  padding: "12px 24px",
  borderRadius: 6,
  fontWeight: 600,
  fontSize: "0.95rem",
  textTransform: "none",
  backgroundColor: "#2f81ad",
  color: "#fff",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#1d648c", boxShadow: "none" },
  transition: "background-color 0.2s ease",
});

const GhostButton = styled(Button)(({ theme }) => ({
  padding: "11px 22px",
  borderRadius: 6,
  fontWeight: 600,
  fontSize: "0.95rem",
  textTransform: "none",
  color: "#fff",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  backgroundColor: "transparent",
  marginLeft: theme.spacing(1.5),
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.06)",
    borderColor: "rgba(255, 255, 255, 0.35)",
  },
  transition: "all 0.2s ease",
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    marginTop: theme.spacing(1.5),
  },
}));

const HeroChips = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(4),
  borderTop: "1px solid rgba(255, 255, 255, 0.08)",
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
}));

const HeroChip = styled("span")({
  fontSize: "0.78rem",
  fontWeight: 600,
  color: "rgba(255, 255, 255, 0.85)",
  padding: "5px 11px",
  borderRadius: 999,
  backgroundColor: "rgba(255, 255, 255, 0.06)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
});

const Label = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#2f81ad",
  marginBottom: 14,
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "2.4rem",
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: "-0.02em",
  color: "#0f172a",
  marginBottom: theme.spacing(2),
  maxWidth: 760,
  [theme.breakpoints.down("md")]: { fontSize: "1.95rem" },
  [theme.breakpoints.down("sm")]: { fontSize: "1.65rem" },
}));

const Lead = styled(Typography)({
  fontSize: "1.02rem",
  lineHeight: 1.65,
  color: "rgba(15, 23, 42, 0.65)",
  maxWidth: 720,
});

const Card = styled(Box)(({ theme }) => ({
  height: "100%",
  padding: theme.spacing(3.5, 3.25),
  borderRadius: 10,
  backgroundColor: "#fff",
  border: "1px solid rgba(15, 23, 42, 0.1)",
  display: "flex",
  flexDirection: "column",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    borderColor: "rgba(47, 129, 173, 0.5)",
    boxShadow: "0 8px 24px -12px rgba(15, 23, 42, 0.15)",
  },
}));

const IconBadge = styled(Box)({
  width: 44,
  height: 44,
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(47, 129, 173, 0.08)",
  color: "#2f81ad",
  marginBottom: 20,
  "& svg": { fontSize: 22 },
});

const CardTitle = styled(Typography)({
  fontSize: "1.1rem",
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: 8,
  letterSpacing: "-0.01em",
});

const CardCopy = styled(Typography)({
  fontSize: "0.92rem",
  lineHeight: 1.6,
  color: "rgba(15, 23, 42, 0.65)",
  marginBottom: 16,
  flex: 1,
});

const TagRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
});

const Tag = styled("span")({
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "#1d648c",
  padding: "3px 9px",
  borderRadius: 6,
  backgroundColor: "rgba(47, 129, 173, 0.08)",
  border: "1px solid rgba(47, 129, 173, 0.18)",
});

const StepIndex = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "rgba(15, 23, 42, 0.4)",
  marginBottom: 12,
});

const Duration = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#2f81ad",
  marginBottom: 12,
});

const ProjectCard = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  backgroundColor: "#fff",
  borderRadius: 10,
  overflow: "hidden",
  border: "1px solid rgba(15, 23, 42, 0.1)",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    borderColor: "rgba(47, 129, 173, 0.5)",
    boxShadow: "0 12px 28px -16px rgba(15, 23, 42, 0.2)",
    "& img": { transform: "scale(1.04)" },
  },
});

const Thumb = styled("div")({
  position: "relative",
  width: "100%",
  aspectRatio: "16 / 10",
  overflow: "hidden",
  backgroundColor: "#f1f5f9",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    transition: "transform 0.4s ease",
  },
});

const TechPill = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "9px 16px",
  borderRadius: 999,
  backgroundColor: "#fff",
  border: "1px solid rgba(15, 23, 42, 0.1)",
  color: "#0f172a",
  fontWeight: 600,
  fontSize: "0.88rem",
});

const HERO_CHIPS = [
  "WordPress",
  "Drupal",
  "Headless CMS",
  "LearnDash",
  "Moodle",
  "Custom LMS",
];

interface Capability {
  icon: React.ReactNode;
  title: string;
  description: string;
  tags: string[];
}

const CAPABILITIES: Capability[] = [
  {
    icon: <ArticleOutlinedIcon />,
    title: "Custom CMS & block-editor builds",
    description:
      "WordPress with bespoke plugins and Gutenberg blocks. Drupal with custom modules. Headless setups on Strapi, Sanity, or Contentful with a React/Next.js front-end.",
    tags: ["WordPress", "Drupal", "Headless", "Gutenberg"],
  },
  {
    icon: <SchoolOutlinedIcon />,
    title: "LMS platforms & course delivery",
    description:
      "LearnDash on WordPress, Moodle deployments, or a fully custom React + Node LMS — with course catalogs, quizzes, certificates, and progress tracking.",
    tags: ["LearnDash", "Moodle", "Custom LMS"],
  },
  {
    icon: <GroupsOutlinedIcon />,
    title: "Users, roles & permissions",
    description:
      "Role-based access control. Student/instructor/admin hierarchies. SSO via SAML, OAuth, or Google Workspace. Multi-site and multi-tenant when needed.",
    tags: ["RBAC", "SSO", "SAML"],
  },
  {
    icon: <PaymentsOutlinedIcon />,
    title: "Payments & subscriptions",
    description:
      "Stripe, PayPal, and WooCommerce integrations. One-time, recurring, and metered billing. Coupons, drip content, and gated courses configured to your business rules.",
    tags: ["Stripe", "WooCommerce", "Subscriptions"],
  },
  {
    icon: <SwapHorizOutlinedIcon />,
    title: "Legacy migrations",
    description:
      "Moving off Joomla, an old WordPress build, or a custom CMS. We migrate content, redirects, users, and integrations — without breaking SEO.",
    tags: ["Content migration", "URL redirects"],
  },
  {
    icon: <AssessmentOutlinedIcon />,
    title: "Analytics & reporting",
    description:
      "Course completion, engagement, drop-off, revenue. GA4, PostHog, or custom dashboards so editors and admins see what's working without bothering engineering.",
    tags: ["GA4", "PostHog", "Dashboards"],
  },
];

const PROCESS = [
  {
    n: "01",
    icon: <SearchOutlinedIcon />,
    title: "Audit & strategy",
    duration: "Week 1",
    description:
      "We map your content workflows, user roles, and integrations. Pick the right platform — off-the-shelf, headless, or custom. Lock scope and price.",
  },
  {
    n: "02",
    icon: <ArchitectureOutlinedIcon />,
    title: "Architecture & modeling",
    duration: "Week 1–2",
    description:
      "Content types, taxonomies, role permissions, and the editor experience are designed before code. Clickable prototype to validate.",
  },
  {
    n: "03",
    icon: <CodeOutlinedIcon />,
    title: "Build & integrate",
    duration: "Week 2–10",
    description:
      "Two-week sprints. Custom plugins, themes, modules, integrations. Working software every Friday, code in your repo from day one.",
  },
  {
    n: "04",
    icon: <RocketLaunchOutlinedIcon />,
    title: "Launch & maintain",
    duration: "Ongoing",
    description:
      "Production deploy, content migration, editor training, and SLA-backed support. We monitor, patch, and ship new features.",
  },
];

const TECHNOLOGIES = [
  "WordPress",
  "Drupal",
  "LearnDash",
  "Moodle",
  "Joomla",
  "WooCommerce",
  "Elementor",
  "Strapi",
  "Sanity",
  "Contentful",
  "Articulate",
  "Custom LMS",
];

interface Project {
  title: string;
  description: string;
  image: string;
  tags: string[];
}

const PROJECTS: Project[] = [
  {
    title: "Corporate training portal",
    description:
      "Multinational LMS with skill tracking, role-based content, and certification management. WordPress + LearnDash with custom plugins and an analytics dashboard.",
    image: brandedPlaceholder("Corporate LMS"),
    tags: ["WordPress", "LearnDash", "Custom Plugins"],
  },
  {
    title: "University CMS",
    description:
      "Drupal-powered website for a university — student portal, course catalog, payment processing, and an editorial workflow used by 30+ contributors.",
    image: brandedPlaceholder("Education CMS"),
    tags: ["Drupal", "Custom Modules", "API"],
  },
  {
    title: "Subscription e-learning platform",
    description:
      "Headless React + Node LMS with Stripe-powered subscriptions, drip content, quizzes, certificates, and an instructor dashboard for live cohort management.",
    image: brandedPlaceholder("E-Learning Platform"),
    tags: ["React", "Node.js", "Stripe"],
  },
];

const CmsLmsPage: React.FC = () => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [capsVisible, setCapsVisible] = useState(false);
  const [processVisible, setProcessVisible] = useState(false);
  const capsRef = useRef<HTMLDivElement>(null);
  const processRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    const watchers: Array<() => void> = [];
    [
      [capsRef, setCapsVisible] as const,
      [processRef, setProcessVisible] as const,
    ].forEach(([ref, setter]) => {
      if (!ref.current) return;
      const obs = new IntersectionObserver(
        ([e]) => e.isIntersecting && setter(true),
        { threshold: 0.15 }
      );
      obs.observe(ref.current);
      watchers.push(() => obs.disconnect());
    });
    return () => watchers.forEach((w) => w());
  }, []);

  return (
    <Box sx={{ bgcolor: "#fff" }}>
      <PageTitle title="CMS & LMS Development" />
      <Navbar />

      <HeroBg component="section" aria-labelledby="cms-heading">
        <Container maxWidth="lg">
          <Box
            sx={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? "translateY(0)" : "translateY(12px)",
              transition: "opacity 0.5s ease, transform 0.5s ease",
            }}
          >
            <HeroLabel>CMS &amp; LMS development</HeroLabel>
            <HeroTitle id="cms-heading" variant="h1">
              Content and learning platforms, built to scale.
            </HeroTitle>
            <HeroLead>
              Custom WordPress, Drupal, and headless CMS builds. LearnDash,
              Moodle, and bespoke LMS platforms. Shipped in 6–12 weeks by
              senior engineers — with full IP transfer.
            </HeroLead>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
              }}
            >
              <PrimaryButton
                endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                onClick={() => navigate("/contacts")}
                disableElevation
              >
                Start your build
              </PrimaryButton>
              <GhostButton onClick={() => navigate("/portfolio")}>
                See our work
              </GhostButton>
            </Box>
            <HeroChips>
              {HERO_CHIPS.map((chip) => (
                <HeroChip key={chip}>{chip}</HeroChip>
              ))}
            </HeroChips>
          </Box>
        </Container>
      </HeroBg>

      <Box
        ref={capsRef}
        component="section"
        sx={{ py: { xs: 8, md: 11 }, backgroundColor: "#fff" }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              mb: { xs: 5, md: 7 },
              opacity: capsVisible ? 1 : 0,
              transform: capsVisible ? "translateY(0)" : "translateY(12px)",
              transition: "opacity 0.5s ease, transform 0.5s ease",
            }}
          >
            <Label>What we build</Label>
            <SectionTitle variant="h2">
              From simple sites to enterprise learning platforms
            </SectionTitle>
            <Lead>
              Six product areas where most of our CMS and LMS engagements
              concentrate. Mix and match — every build is scoped to what you
              actually need.
            </Lead>
          </Box>

          <Grid container spacing={2.5}>
            {CAPABILITIES.map((cap, i) => (
              <Grid item xs={12} sm={6} md={4} key={cap.title}>
                <Box
                  sx={{
                    height: "100%",
                    opacity: capsVisible ? 1 : 0,
                    transform: capsVisible ? "translateY(0)" : "translateY(12px)",
                    transition: `opacity 0.45s ease ${
                      0.05 + i * 0.05
                    }s, transform 0.45s ease ${0.05 + i * 0.05}s`,
                  }}
                >
                  <Card>
                    <IconBadge>{cap.icon}</IconBadge>
                    <CardTitle>{cap.title}</CardTitle>
                    <CardCopy>{cap.description}</CardCopy>
                    <TagRow>
                      {cap.tags.map((t) => (
                        <Tag key={t}>{t}</Tag>
                      ))}
                    </TagRow>
                  </Card>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box
        ref={processRef}
        component="section"
        sx={{ py: { xs: 8, md: 11 }, backgroundColor: "#f8fafc" }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              mb: { xs: 5, md: 7 },
              opacity: processVisible ? 1 : 0,
              transform: processVisible
                ? "translateY(0)"
                : "translateY(12px)",
              transition: "opacity 0.5s ease, transform 0.5s ease",
            }}
          >
            <Label>How we build it</Label>
            <SectionTitle variant="h2">
              Four stages, predictable delivery
            </SectionTitle>
            <Lead>
              Every CMS and LMS engagement runs the same four-stage process.
              You always know what's happening this week, what's next, and
              when it ships.
            </Lead>
          </Box>

          <Grid container spacing={2.5}>
            {PROCESS.map((step, i) => (
              <Grid item xs={12} sm={6} md={3} key={step.n}>
                <Box
                  sx={{
                    height: "100%",
                    opacity: processVisible ? 1 : 0,
                    transform: processVisible
                      ? "translateY(0)"
                      : "translateY(12px)",
                    transition: `opacity 0.45s ease ${
                      0.05 + i * 0.06
                    }s, transform 0.45s ease ${0.05 + i * 0.06}s`,
                  }}
                >
                  <Card>
                    <IconBadge>{step.icon}</IconBadge>
                    <StepIndex>Step {step.n}</StepIndex>
                    <CardTitle>{step.title}</CardTitle>
                    <Duration>{step.duration}</Duration>
                    <CardCopy sx={{ mb: 0 }}>{step.description}</CardCopy>
                  </Card>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{ py: { xs: 8, md: 11 }, backgroundColor: "#fff" }}
      >
        <Container maxWidth="lg">
          <Box sx={{ mb: { xs: 4, md: 5 } }}>
            <Label>Platforms &amp; tools</Label>
            <SectionTitle variant="h2">What we work with</SectionTitle>
            <Lead>
              No religious wars over platforms. We pick the stack that fits
              your team's skills, content workflows, and budget.
            </Lead>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
            {TECHNOLOGIES.map((t) => (
              <TechPill key={t}>
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 16, color: "#2f81ad" }}
                />
                {t}
              </TechPill>
            ))}
          </Box>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{ py: { xs: 8, md: 11 }, backgroundColor: "#f8fafc" }}
      >
        <Container maxWidth="lg">
          <Box sx={{ mb: { xs: 5, md: 7 } }}>
            <Label>Case studies</Label>
            <SectionTitle variant="h2">
              CMS &amp; LMS projects we've shipped
            </SectionTitle>
          </Box>

          <Grid container spacing={2.5}>
            {PROJECTS.map((p) => (
              <Grid item xs={12} sm={6} md={4} key={p.title}>
                <ProjectCard>
                  <Thumb>
                    <img src={p.image} alt={p.title} loading="lazy" />
                  </Thumb>
                  <Box
                    sx={{
                      p: 3,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardTitle>{p.title}</CardTitle>
                    <CardCopy sx={{ flex: 1 }}>{p.description}</CardCopy>
                    <TagRow>
                      {p.tags.map((t) => (
                        <Tag key={t}>{t}</Tag>
                      ))}
                    </TagRow>
                  </Box>
                </ProjectCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <LetsTalk />
      <Footer />
    </Box>
  );
};

export default CmsLmsPage;
